import { Injectable, Inject, InjectionToken } from '@angular/core';
import { FormlyGroup } from '../components/formly.group';
import { Field } from './../templates/field';
import { reverseDeepMerge } from './../utils';
import { FormlyFieldConfig } from '../components/formly.field.config';

export const /** @type {?} */ FORMLY_CONFIG_TOKEN = new InjectionToken<FormlyConfig>('FORMLY_CONFIG_TOKEN');
/**
 * Maintains list of formly field directive types. This can be used to register new field templates.
 */
export class FormlyConfig {
  types: {[name: string]: TypeOption} = {
    'formly-group': {
      name: 'formly-group',
      component: FormlyGroup,
    },
  };
  validators: {[name: string]: ValidatorOption} = {};
  wrappers: {[name: string]: WrapperOption} = {};
  messages: { [name: string]: string; } = {};

  templateManipulators: {
    preWrapper: ManipulatorWrapper[];
    postWrapper: ManipulatorWrapper[];
  } = {
    preWrapper: [],
    postWrapper: [],
  };

  extras: {
    fieldTransform?: any,
    showError?: (field: Field) => boolean;
  } = {
    fieldTransform: undefined,
    showError: function(field: Field) {
      return (field.formControl.touched || (field.field.validation && field.field.validation.show)) && !field.formControl.valid;
    },
  };
/**
 * @param {?=} configs
 */
constructor( configs: ConfigOption[] = []) {
    configs.map(config => this.addConfig(config));
  }
/**
 * @param {?} config
 * @return {?}
 */
addConfig(config: ConfigOption) {
    if (config.types) {
      config.types.map(type => this.setType(type));
    }
    if (config.validators) {
      config.validators.map(validator => this.setValidator(validator));
    }
    if (config.wrappers) {
      config.wrappers.map(wrapper => this.setWrapper(wrapper));
    }
    if (config.manipulators) {
      config.manipulators.map(manipulator => this.setManipulator(manipulator));
    }
    if (config.validationMessages) {
      config.validationMessages.map(validation => this.addValidatorMessage(validation.name, validation.message));
    }
    if (config.extras) {
      this.extras = { ...this.extras, ...config.extras };
    }
  }
/**
 * @param {?} options
 * @return {?}
 */
setType(options: TypeOption | TypeOption[]) {
    if (Array.isArray(options)) {
      options.map((option) => this.setType(option));
    } else {
      if (!this.types[options.name]) {
        this.types[options.name] = /** @type {?} */(( <TypeOption>{}));
      }
      this.types[options.name].component = options.component;
      this.types[options.name].name = options.name;
      this.types[options.name].extends = options.extends;
      this.types[options.name].defaultOptions = options.defaultOptions;
      if (options.wrappers) {
        options.wrappers.map((wrapper) => this.setTypeWrapper(options.name, wrapper));
      }
    }
  }
/**
 * @param {?} name
 * @return {?}
 */
getType(name: string): TypeOption {
    if (!this.types[name]) {
      throw new Error(`[Formly Error] There is no type by the name of "${name}"`);
    }

    this.mergeExtendedType(name);

    return this.types[name];
  }
/**
 * @param {?=} field
 * @return {?}
 */
getMergedField(field: FormlyFieldConfig = {}): any {
    let /** @type {?} */ name = field.type;
    if (!this.types[name]) {
      throw new Error(`[Formly Error] There is no type by the name of "${name}"`);
    }

    this.mergeExtendedType(name);
    if (this.types[name].defaultOptions) {
      reverseDeepMerge(field, this.types[name].defaultOptions);
    }

    let /** @type {?} */ extendDefaults = this.types[name].extends && this.getType(this.types[name].extends).defaultOptions;
    if (extendDefaults) {
      reverseDeepMerge(field, extendDefaults);
    }

    if (field && field.optionsTypes) {
      field.optionsTypes.map(option => {
        let /** @type {?} */ defaultOptions = this.getType(option).defaultOptions;
        if (defaultOptions) {
          reverseDeepMerge(field, defaultOptions);
        }
      });
    }
    reverseDeepMerge(field, this.types[name]);
  }
/**
 * @param {?} options
 * @return {?}
 */
setWrapper(options: WrapperOption) {
    this.wrappers[options.name] = options;
    if (options.types) {
      options.types.map((type) => {
        this.setTypeWrapper(type, options.name);
      });
    }
  }
/**
 * @param {?} name
 * @return {?}
 */
getWrapper(name: string): WrapperOption {
    if (!this.wrappers[name]) {
      throw new Error(`[Formly Error] There is no wrapper by the name of "${name}"`);
    }

    return this.wrappers[name];
  }
/**
 * @param {?} type
 * @param {?} name
 * @return {?}
 */
setTypeWrapper(type: string, name: string) {
    if (!this.types[type]) {
      this.types[type] = /** @type {?} */(( <TypeOption>{}));
    }
    if (!this.types[type].wrappers) {
      this.types[type].wrappers = /** @type {?} */(( <[string]>[]));
    }
    this.types[type].wrappers.push(name);
  }
/**
 * @param {?} options
 * @return {?}
 */
setValidator(options: ValidatorOption) {
    this.validators[options.name] = options;
  }
/**
 * @param {?} name
 * @return {?}
 */
getValidator(name: string): ValidatorOption {
    if (!this.validators[name]) {
      throw new Error(`[Formly Error] There is no validator by the name of "${name}"`);
    }

    return this.validators[name];
  }
/**
 * @param {?} name
 * @param {?} message
 * @return {?}
 */
addValidatorMessage(name: string, message: string) {
    this.messages[name] = message;
  }
/**
 * @param {?} name
 * @return {?}
 */
getValidatorMessage(name: string) {
    return this.messages[name];
  }
/**
 * @param {?} manipulator
 * @return {?}
 */
setManipulator(manipulator: ManipulatorOption) {
    new manipulator.class()[manipulator.method](this);
  }
/**
 * @param {?} name
 * @return {?}
 */
private mergeExtendedType(name: string) {
    if (!this.types[name].extends) {
      return;
    }

    const /** @type {?} */ extendedType = this.getType(this.types[name].extends);
    if (!this.types[name].component) {
      this.types[name].component = extendedType.component;
    }

    if (!this.types[name].wrappers) {
      this.types[name].wrappers = extendedType.wrappers;
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Array, decorators: [{ type: Inject, args: [FORMLY_CONFIG_TOKEN, ] }, ]},
];
}

function FormlyConfig_tsickle_Closure_declarations() {
/** @type {?} */
FormlyConfig.decorators;
/**
 * @nocollapse
 * @type {?}
 */
FormlyConfig.ctorParameters;
/** @type {?} */
FormlyConfig.prototype.types;
/** @type {?} */
FormlyConfig.prototype.validators;
/** @type {?} */
FormlyConfig.prototype.wrappers;
/** @type {?} */
FormlyConfig.prototype.messages;
/** @type {?} */
FormlyConfig.prototype.templateManipulators;
/** @type {?} */
FormlyConfig.prototype.extras;
}

export interface TypeOption {
  name: string;
  component?: any;
  wrappers?: string[];
  extends?: string;
  defaultOptions?: FormlyFieldConfig;
}

export interface WrapperOption {
  name: string;
  component: any;
  types?: string[];
}

export interface ValidatorOption {
  name: string;
  validation: any;
}

export interface ValidationMessageOption {
  name: string;
  message: any;
}

export interface ManipulatorOption {
  class?: { new (): any };
  method?: string;
}

export interface ManipulatorWrapper {
  (f: FormlyFieldConfig): string;
}

export interface ConfigOption {
  types?: TypeOption[];
  wrappers?: WrapperOption[];
  validators?: ValidatorOption[];
  validationMessages?: ValidationMessageOption[];
  manipulators?: ManipulatorOption[];
  extras?: {
    fieldTransform?: any,
    showError?: (field: FormlyFieldConfig) => boolean;
  };
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
